import { IconButton, Stack, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DocumentSectionRow, useSectionOpen, type ExpandSignal } from '../../shared/components/DocumentSections';
import { Pill } from '../../shared/components/Pill';
import type { DocumentSection, DocumentSectionConfidence } from '../../core/types/data-models';
import { scoreBand, type ScoreBand } from './scoring';

const BAND_PILL_COLOR: Record<ScoreBand, 'success' | 'warning' | 'error'> = {
  strong: 'success',
  mixed: 'warning',
  weak: 'error',
};

const CONFIDENCE_LABEL: Record<DocumentSectionConfidence, string> = {
  HIGH: 'High confidence',
  MEDIUM: 'Medium confidence',
  LOW: 'Low confidence',
};

interface ValidationSectionRowProps {
  section: DocumentSection;
  childrenByParent: Map<number, DocumentSection[]>;
  depth?: number;
  expandSignal: ExpandSignal | null;
  canEdit: boolean;
  onEdit: (section: DocumentSection) => void;
}

/**
 * One section of a validation-stage document: the shared document row with
 * the verdict score and the confidence (root sections only — the question is
 * the root, sub-sections are never scored) and an edit button at the end of
 * the title row. No status and no hover toolbar — this view does not deal in
 * the document workflow. Starts open so a stage's result reads at once.
 */
export function ValidationSectionRow({
  section,
  childrenByParent,
  depth = 0,
  expandSignal,
  canEdit,
  onEdit,
}: ValidationSectionRowProps) {
  const children = (childrenByParent.get(section.id) ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const [open, setOpen] = useSectionOpen(true, expandSignal);
  const isRoot = section.parentDocumentSectionId == null;

  return (
    <DocumentSectionRow
      section={section}
      depth={depth}
      open={open}
      onToggle={() => setOpen((o) => !o)}
      trailing={
        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, flexShrink: 0 }}>
          {isRoot && (
            <>
              <Tooltip title={section.verdictReason ?? 'Verdict score: how positive this section is for the case, from 1 (negative) to 10 (positive)'}>
                <span>
                  <Pill color={BAND_PILL_COLOR[scoreBand(section.verdictScore)]}>{section.verdictScore}/10</Pill>
                </span>
              </Tooltip>
              <Pill mono={false}>{CONFIDENCE_LABEL[section.confidence] ?? CONFIDENCE_LABEL.MEDIUM}</Pill>
            </>
          )}
          {canEdit && (
            <Tooltip title="Edit content">
              <IconButton
                size="small"
                sx={{ width: 26, height: 26 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(section);
                }}
                aria-label={`Edit ${section.title}`}
              >
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }
    >
      {children.map((child) => (
        <ValidationSectionRow
          key={child.id}
          section={child}
          childrenByParent={childrenByParent}
          depth={depth + 1}
          expandSignal={expandSignal}
          canEdit={canEdit}
          onEdit={onEdit}
        />
      ))}
    </DocumentSectionRow>
  );
}
